import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { AgGridAngular } from 'ag-grid-angular';
import { RowNode } from 'ag-grid-community';
import SweetAlert2 from 'sweetalert2';

import { NSMessages } from '@ruby/configs/messages.constants';
import { NSTrackMoveConstants } from '@ruby/configs/track-move.constants';
import { ITrackAction, ITrackInventory, ITrackMovePayload } from '@ruby/modules/track-move/models/track-move.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { ETrackAction } from '@ruby/shared/enums/track-move.enums';
import { ITrack } from '@ruby/shared/models/inventory-management/inventory-header.interface';
import { InventoryManagementUtils } from '@ruby/shared/services/utils/inventory-management/inventory-management.utils';

@Component({
  selector: 'ng-from-track-header',
  templateUrl: './from-track-header.component.html',
  styleUrls: ['./from-track-header.component.scss']
})
export class FromTrackHeaderComponent implements OnChanges {
  @Input() fromTrackGrid!: AgGridAngular;
  @Input() toTrackGrid!: AgGridAngular;
  @Input() fromTrackData!: Array<ITrackInventory>;
  @Input() toTrackData!: Array<ITrackInventory>;
  @Input() inventoryTracks!: Array<ITrack>;
  @Input() selectedToTrack?: ITrack;
  @Output() fromTrackSelected = new EventEmitter<ITrack>();
  @Output() move = new EventEmitter<ITrackMovePayload>();
  @Output() refresh = new EventEmitter<ITrackMovePayload>();
  @Output() adjust = new EventEmitter<{ fromTrackEquipments: Array<string>; toTrackEquipments: Array<string>; payload: ITrackMovePayload }>();
  trackConfig: ITrackAction = NSTrackMoveConstants.trackAction;
  selectedFromTrack?: ITrack;

  constructor(private inventoryUtilsSvc: InventoryManagementUtils) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fromTrackGrid && changes.fromTrackGrid.currentValue) {
      this.fromTrackGrid.api.addEventListener('selectionChanged', this.updateActionConfig.bind(this));
      this.fromTrackGrid.api.addEventListener('rowDragEnd', this.onRowDragEnd.bind(this));
      this.fromTrackGrid.api.addEventListener('rowDataUpdated', this.updateActionConfig.bind(this));
    }

    if (changes.inventoryTracks && changes.inventoryTracks.currentValue.length > 0) {
      this.selectedFromTrack = this.inventoryTracks.find((track: ITrack) => track.id === this.selectedFromTrack?.id);
      if (this.fromTrackGrid && this.fromTrackGrid.api) {
        this.updateActionConfig();
      }
    }
  }

  /**
   * Emit Track
   *
   * @summary Emits selected track
   * @param $event: MatSelectChange
   * @returns void
   */
  emitTrack($event: MatSelectChange): void {
    $event.source.close();
    this.selectedFromTrack = $event.value;
    this.trackConfig = NSTrackMoveConstants.trackAction;
    this.fromTrackSelected.emit($event.value);
  }

  /**
   * On Row Drag End
   *
   * @summary Reset's track action config
   * @return void
   */
  onRowDragEnd(): void {
    this.trackConfig = NSTrackMoveConstants.trackAction;
  }

  /**
   * Update Action Config
   *
   * @summary Update's track action config
   * @return void
   */
  updateActionConfig(): void {
    this.trackConfig = this.inventoryUtilsSvc.resetTrackActions(
      this.fromTrackGrid.api.getSelectedNodes(),
      this.selectedFromTrack ? this.selectedFromTrack.mainTrack : false,
      this.fromTrackGrid.api.getDisplayedRowCount()
    );
  }

  /**
   * Emit Move
   *
   * @summary Validated selected tracks and emits move
   * @return void
   */
  emitMove(): void {
    const itemsToUpdate: Array<ITrackInventory> = [];
    const tracks: Set<string> = new Set<string>();
    const equipments: Set<string> = new Set<string>();
    this.fromTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
      itemsToUpdate.push({ ...node.data, position: 0 });
      tracks.add(node.data.id);
      if (!node.data.toTrack) {
        equipments.add(node.data.equipmentId);
      }
    });
    if (tracks.size > 1) {
      this.showAlert('Alert', NSMessages.multipleTrackSelected);
    } else if (equipments.size > 0) {
      this.showAlert(NSMessages.toTrackNotSelected, Array.from(equipments).join(', '));
    } else {
      this.move.emit({
        fromTrack: this.selectedFromTrack,
        inventoryToMove: itemsToUpdate
      } as ITrackMovePayload);
    }
  }

  /**
   * Emit Adjust
   *
   * @summary Validated selected tracks and emits adjust
   * @param $action: string
   * @return void
   */
  emitAdjust($action: string): void {
    const itemsToUpdate: Array<ITrackInventory> = [];
    const fromTrackEquipments: Array<string> = [];
    this.fromTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
      itemsToUpdate.push({ ...node.data, toTrack: undefined });
      fromTrackEquipments.push(node.data.equipmentId);
    });
    this.adjust.emit({
      fromTrackEquipments,
      toTrackEquipments: this.getToTrackEquipments(),
      payload: {
        fromTrack: this.selectedFromTrack,
        toTrack: this.selectedToTrack,
        inventoryToMove: itemsToUpdate,
        action: $action as ETrackAction
      } as ITrackMovePayload
    });
  }

  /**
   * Emit Refresh
   *
   * @summary Validated selected tracks and emits refresh
   * @return void
   */
  emitRefresh(): void {
    this.refresh.emit({ fromTrack: this.selectedFromTrack } as ITrackMovePayload);
  }

  /**
   * Flip
   *
   * @summary Validated selected tracks
   * @return void
   */
  flip(): void {
    const lineNumbers: Array<number> = [];
    const rows: Array<RowNode> = this.fromTrackGrid.api.getSelectedNodes();
    rows.forEach((node: RowNode) => {
      lineNumbers.push(node.data.lineNumber);
    });
    const dontAllow: boolean = this.inventoryUtilsSvc.validLineNumbers(lineNumbers);
    if (dontAllow) {
      this.showAlert('Alert', NSMessages.consecutiveTrack);
    } else {
      this.emitAdjust(ETrackAction.FLIP);
    }
  }

  private getToTrackEquipments(): Array<string> {
    const toTrackEquipments: Array<string> = [];
    if (this.selectedToTrack) {
      this.toTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
        toTrackEquipments.push(node.data.equipmentId);
      });
    }
    this.toTrackGrid.api.setRowData([]);
    this.toTrackGrid.api.setRowData(this.toTrackData);
    this.fromTrackGrid.api.setRowData([]);
    this.fromTrackGrid.api.setRowData(this.fromTrackData);
    return toTrackEquipments;
  }

  private showAlert(title: string, text: string): void {
    SweetAlert2.fire({ title, text, icon: 'warning', heightAuto: false, confirmButtonColor: EColors.GOLD });
  }
}
